import { useMemo } from 'react'

import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidOfferPriceCommonData } from '@/features/bidding/types/bidOfferPriceFlow'
import getBidConfig from '@/features/bidding/utils/getBidConfig'
import { getDraftOrOfferedBidsMatchingActivationGroups } from '@/features/bidding/utils/getDraftOrOfferedBidsMatchingActivationGroups'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBid } from '@/features/bidding/utils/model/buildAggregatedBid'
import { buildAggregatedBid } from '@/features/bidding/utils/model/buildAggregatedBid'

import { useBidOfferEligibleActivationGroups } from './useBidOfferEligibleActivationGroups'
import { useBidOfferPriceBidsWithPrices } from './useBidOfferPriceBidsWithPrices'

export type BidOfferPriceData_Aggregated = {
  common: BidOfferPriceCommonData
  bidData: {
    draftBidsMatchingActivationGroups: ExtendedBid[]
    initialAggregatedBid: AggregatedBid
  }
}

export type UseBidOfferPriceDataAggregatedReturn =
  | { isLoading: true; data: null }
  | { isLoading: false; data: BidOfferPriceData_Aggregated }

/**
 * Hook that fetches and computes data for the aggregated price flow.
 *
 * This hook is used when prices are aggregated across all activation groups
 * (i.e., when requiresPriceByActivationGroup is false).
 *
 * @param groupParams - Grouped bid parameters (already loaded by parent)
 * @returns Data needed for the aggregated price flow, including common data and aggregated bid
 */
export function useBidOfferPriceData_Aggregated(groupParams: GroupedBidParams): UseBidOfferPriceDataAggregatedReturn {
  const { selectedCountry } = useBiddingContext()

  // 1. Fetch bids with aggregated prices (not per activation group)
  const { bidsWithPrices, isLoading: isLoadingBids } = useBidOfferPriceBidsWithPrices({
    groupParams,
    requiresPriceByActivationGroup: false,
  })

  // 2. Fetch eligible activation groups matching the groupParams
  const { eligibleActivationGroups, isLoading: isLoadingActivationGroups } =
    useBidOfferEligibleActivationGroups(groupParams)

  // 3. Compute derived data - filter draft bids
  const draftBidsMatchingActivationGroups = useMemo(
    () => getDraftOrOfferedBidsMatchingActivationGroups(bidsWithPrices, eligibleActivationGroups ?? []),
    [bidsWithPrices, eligibleActivationGroups],
  )

  // 4. Compute initial aggregated bid
  const initialAggregatedBid = useMemo(() => {
    const { priceLadderType } = getBidConfig(selectedCountry, groupParams.bidType)
    return buildAggregatedBid(draftBidsMatchingActivationGroups, priceLadderType)
  }, [selectedCountry, groupParams.bidType, draftBidsMatchingActivationGroups])

  // 5. Compute the final data object (always, before any early returns)
  const data = useMemo(
    () => ({
      isLoading: false as const,
      data: {
        common: {
          eligibleActivationGroups: eligibleActivationGroups ?? [],
        },
        bidData: {
          draftBidsMatchingActivationGroups,
          initialAggregatedBid,
        },
      },
    }),
    [eligibleActivationGroups, draftBidsMatchingActivationGroups, initialAggregatedBid],
  )

  // Check if still loading
  if (isLoadingBids || isLoadingActivationGroups) {
    return { isLoading: true, data: null }
  }

  return data
}
